import { useEffect, useState } from "react";
import { Copy, Key, Loader2, Terminal, Trash2 } from "lucide-react";

import { useUserRole } from "@foundation/altzero_base/base/hooks/useUserRole";
import { useToast } from "@foundation/altzero_base/base/hooks/use-toast";
import { Button } from "@foundation/altzero_base/base/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@foundation/altzero_base/base/components/ui/card";
import { Input } from "@foundation/altzero_base/base/components/ui/input";
import { Label } from "@foundation/altzero_base/base/components/ui/label";
import { Badge } from "@foundation/altzero_base/base/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@foundation/altzero_base/base/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@foundation/altzero_base/base/components/ui/dialog";

import {
  createApiKey,
  listApiKeys,
  revokeApiKey,
} from "../services/apiKeyService";
import type { ApiKeyListItem, ApiKeyScope, CreatedApiKey } from "../types";

const CHAT_ENDPOINT = `${window.location.origin}/api/charlie-chat`;

type SnippetLang = "curl" | "js" | "ts" | "python" | "php" | "ruby" | "go" | "csharp" | "java";

const SNIPPET_LABELS: Record<SnippetLang, string> = {
  curl: "cURL",
  js: "JavaScript",
  ts: "TypeScript",
  python: "Python",
  php: "PHP",
  ruby: "Ruby",
  go: "Go",
  csharp: "C#",
  java: "Java",
};

const buildSnippet = (lang: SnippetLang, url: string): string => {
  switch (lang) {
    case "curl":
      return `curl -X POST ${url} \\
  -H "Authorization: Bearer ak_YOUR_KEY_HERE" \\
  -H "Content-Type: application/json" \\
  -d '{"messages":[{"role":"user","content":"What are concussion red flags?"}]}'`;

    case "js":
      return `const response = await fetch('${url}', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ak_YOUR_KEY_HERE',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    messages: [{ role: 'user', content: 'What are concussion red flags?' }],
  }),
});
const data = await response.json();
console.log(data);`;

    case "ts":
      return `type ChatResponse = {
  content: string;
  sources: unknown[];
  provider: string;
  skillApplied: boolean;
  skillName: string | null;
};

const response = await fetch('${url}', {
  method: 'POST',
  headers: {
    Authorization: 'Bearer ak_YOUR_KEY_HERE',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    messages: [{ role: 'user', content: 'What are concussion red flags?' }],
  }),
});
const data: ChatResponse = await response.json();
console.log(data);`;

    case "python":
      return `import requests

response = requests.post(
    '${url}',
    headers={
        'Authorization': 'Bearer ak_YOUR_KEY_HERE',
        'Content-Type': 'application/json',
    },
    json={
        'messages': [{'role': 'user', 'content': 'What are concussion red flags?'}]
    },
)
print(response.json())`;

    case "php":
      return `<?php
$ch = curl_init('${url}');
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST           => true,
    CURLOPT_HTTPHEADER     => [
        'Authorization: Bearer ak_YOUR_KEY_HERE',
        'Content-Type: application/json',
    ],
    CURLOPT_POSTFIELDS     => json_encode([
        'messages' => [['role' => 'user', 'content' => 'What are concussion red flags?']],
    ]),
]);
echo curl_exec($ch);
curl_close($ch);`;

    case "ruby":
      return `require 'net/http'
require 'json'

uri  = URI('${url}')
http = Net::HTTP.new(uri.host, uri.port)
http.use_ssl = uri.scheme == 'https'

req = Net::HTTP::Post.new(uri.path, {
  'Authorization' => 'Bearer ak_YOUR_KEY_HERE',
  'Content-Type'  => 'application/json',
})
req.body = { messages: [{ role: 'user', content: 'What are concussion red flags?' }] }.to_json

puts JSON.parse(http.request(req).body)`;

    case "go":
      return `package main

import (
	"bytes"
	"encoding/json"
	"fmt"
	"net/http"
)

func main() {
	body, _ := json.Marshal(map[string]any{
		"messages": []map[string]string{
			{"role": "user", "content": "What are concussion red flags?"},
		},
	})

	req, _ := http.NewRequest(http.MethodPost, "${url}", bytes.NewBuffer(body))
	req.Header.Set("Authorization", "Bearer ak_YOUR_KEY_HERE")
	req.Header.Set("Content-Type", "application/json")

	resp, _ := http.DefaultClient.Do(req)
	defer resp.Body.Close()

	var result map[string]any
	json.NewDecoder(resp.Body).Decode(&result)
	fmt.Println(result)
}`;

    case "csharp":
      return `using System.Net.Http.Headers;
using System.Text;
using System.Text.Json;

var client = new HttpClient();
client.DefaultRequestHeaders.Authorization =
    new AuthenticationHeaderValue("Bearer", "ak_YOUR_KEY_HERE");

var payload = new {
    messages = new[] { new { role = "user", content = "What are concussion red flags?" } }
};

var response = await client.PostAsync(
    "${url}",
    new StringContent(JsonSerializer.Serialize(payload), Encoding.UTF8, "application/json")
);
Console.WriteLine(await response.Content.ReadAsStringAsync());`;

    case "java":
      return `import java.net.URI;
import java.net.http.*;
import java.net.http.HttpResponse.BodyHandlers;

var client  = HttpClient.newHttpClient();
var payload = """
    {"messages":[{"role":"user","content":"What are concussion red flags?"}]}
    """;

var request = HttpRequest.newBuilder()
    .uri(URI.create("${url}"))
    .header("Authorization", "Bearer ak_YOUR_KEY_HERE")
    .header("Content-Type", "application/json")
    .POST(HttpRequest.BodyPublishers.ofString(payload))
    .build();

System.out.println(client.send(request, BodyHandlers.ofString()).body());`;
  }
};

const DEFAULT_EXPIRY_DAYS = 90;

const formatDate = (date: Date) =>
  date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const defaultExpiryIso = () => {
  const expiry = new Date();
  expiry.setDate(expiry.getDate() + DEFAULT_EXPIRY_DAYS);
  return expiry.toISOString().slice(0, 10); // yyyy-mm-dd for <input type="date">
};

const statusOf = (key: ApiKeyListItem): { label: string; tone: "active" | "muted" | "danger" } => {
  if (key.isRevoked) return { label: "Revoked", tone: "danger" };
  if (key.isExpired) return { label: "Expired", tone: "muted" };
  return { label: "Active", tone: "active" };
};

type Section = "personal" | "org";

type SectionState = {
  keys: ApiKeyListItem[];
  loading: boolean;
  error: string | null;
};

const initialSectionState: SectionState = { keys: [], loading: true, error: null };

export const ApiKeysPage = () => {
  const { isAdmin, isLoading: roleLoading } = useUserRole();
  const { toast } = useToast();

  const [personal, setPersonal] = useState<SectionState>(initialSectionState);
  const [org, setOrg] = useState<SectionState>(initialSectionState);

  const [createName, setCreateName] = useState("");
  const [createExpiry, setCreateExpiry] = useState(defaultExpiryIso());
  const [createScope, setCreateScope] = useState<ApiKeyScope>("personal");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [revealed, setRevealed] = useState<CreatedApiKey | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<ApiKeyListItem | null>(null);
  const [revoking, setRevoking] = useState(false);

  const loadPersonal = async () => {
    setPersonal((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const keys = await listApiKeys("personal");
      setPersonal({ keys, loading: false, error: null });
    } catch (err) {
      setPersonal({
        keys: [],
        loading: false,
        error: err instanceof Error ? err.message : "Failed to load personal keys",
      });
    }
  };

  const loadOrg = async () => {
    setOrg((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const keys = await listApiKeys("org");
      setOrg({ keys, loading: false, error: null });
    } catch (err) {
      setOrg({
        keys: [],
        loading: false,
        error: err instanceof Error ? err.message : "Failed to load organization keys",
      });
    }
  };

  useEffect(() => {
    loadPersonal();
  }, []);

  useEffect(() => {
    if (isAdmin) loadOrg();
  }, [isAdmin]);

  // Non-admins can never create org-scoped keys; force scope back if it slipped through.
  useEffect(() => {
    if (!isAdmin && createScope === "org") {
      setCreateScope("personal");
    }
  }, [isAdmin, createScope]);

  const handleCreate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreateError(null);

    if (!createName.trim()) {
      setCreateError("Name is required");
      return;
    }

    // Interpret yyyy-mm-dd as the user's local calendar day (avoid UTC parsing shifting the day).
    const [y, mo, d] = createExpiry.split("-").map((s) => parseInt(s, 10));
    if (!y || !mo || !d) {
      setCreateError("Invalid expiry date");
      return;
    }
    const expiryDate = new Date(y, mo - 1, d, 23, 59, 59, 999);
    if (Number.isNaN(expiryDate.getTime()) || expiryDate <= new Date()) {
      setCreateError("Expiry must be a future date");
      return;
    }

    setCreating(true);
    try {
      const newKey = await createApiKey({
        name: createName.trim(),
        expiresAt: expiryDate,
        scope: createScope,
      });
      setRevealed(newKey);
      setCreateName("");
      setCreateExpiry(defaultExpiryIso());
      // Refresh affected list
      if (newKey.scope === "org") {
        await loadOrg();
      } else {
        await loadPersonal();
      }
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create API key");
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeConfirm = async () => {
    if (!pendingRevoke) return;
    setRevoking(true);
    try {
      await revokeApiKey(pendingRevoke.id);
      toast({ title: "API key revoked", description: pendingRevoke.name });
      const wasOrg = pendingRevoke.scope === "org";
      setPendingRevoke(null);
      if (wasOrg) {
        await loadOrg();
      } else {
        await loadPersonal();
      }
    } catch (err) {
      toast({
        title: "Failed to revoke",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setRevoking(false);
    }
  };

  const copyKey = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed.key);
      toast({ title: "Copied", description: "API key copied to clipboard" });
    } catch {
      toast({
        title: "Copy failed",
        description: "Select the key text and copy manually",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8 space-y-6">
      <header>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Key className="h-7 w-7" />
          API Keys
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage API keys for programmatic access to Charlie. Treat keys like passwords —
          they grant access to your account or organization.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Create a new key</CardTitle>
          <CardDescription>
            Personal keys act on your behalf. Organization keys (admin only) act at tenant
            scope and have no individual user context.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="grid gap-4 md:grid-cols-[2fr,1fr,1fr,auto]">
            <div className="space-y-1.5">
              <Label htmlFor="api-key-name">Name</Label>
              <Input
                id="api-key-name"
                placeholder="e.g. Partner integration"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                disabled={creating}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="api-key-expiry">Expires</Label>
              <Input
                id="api-key-expiry"
                type="date"
                value={createExpiry}
                onChange={(e) => setCreateExpiry(e.target.value)}
                disabled={creating}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="api-key-scope">Scope</Label>
              <select
                id="api-key-scope"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={createScope}
                onChange={(e) => setCreateScope(e.target.value as ApiKeyScope)}
                disabled={creating || roleLoading}
              >
                <option value="personal">Personal</option>
                {isAdmin && <option value="org">Organization</option>}
              </select>
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={creating}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create key"}
              </Button>
            </div>
          </form>
          {createError && (
            <p className="mt-3 text-sm text-destructive">{createError}</p>
          )}
        </CardContent>
      </Card>

      <QuickStartCard />

      <ApiKeysList
        title="Personal API keys"
        description="Keys bound to your user account. Only you can see and revoke these."
        section="personal"
        state={personal}
        onRefresh={loadPersonal}
        onRevoke={setPendingRevoke}
      />

      {isAdmin && (
        <ApiKeysList
          title="Organization API keys"
          description="Keys bound to the whole organization. Visible to and revocable by admins only."
          section="org"
          state={org}
          onRefresh={loadOrg}
          onRevoke={setPendingRevoke}
        />
      )}

      {/* Reveal-once dialog */}
      <Dialog open={!!revealed} onOpenChange={(open) => !open && setRevealed(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API key created</DialogTitle>
            <DialogDescription>
              Copy this key now — it will not be shown again. Anyone with this key can act as
              {revealed?.scope === "personal" ? " you" : " your organization"}.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted p-3 font-mono text-sm break-all">
            {revealed?.key}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={copyKey}>
              <Copy className="h-4 w-4" /> Copy
            </Button>
            <Button onClick={() => setRevealed(null)}>I've saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm revoke */}
      <AlertDialog
        open={!!pendingRevoke}
        onOpenChange={(open) => !open && setPendingRevoke(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this API key?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRevoke?.name} will stop working immediately. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevokeConfirm} disabled={revoking}>
              {revoking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Revoke"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const QuickStartCard = () => {
  const [lang, setLang] = useState<SnippetLang>("curl");
  const { toast } = useToast();

  const snippet = buildSnippet(lang, CHAT_ENDPOINT);

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast({ title: "Copied", description: "Code snippet copied to clipboard" });
    } catch {
      toast({ title: "Copy failed", description: "Select the text and copy manually", variant: "destructive" });
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(CHAT_ENDPOINT);
      toast({ title: "Copied", description: "Endpoint URL copied" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Terminal className="h-5 w-5" />
          Test your key
        </CardTitle>
        <CardDescription>
          Send a chat message using your API key. Replace{" "}
          <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">ak_YOUR_KEY_HERE</code>{" "}
          with a key from the list below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Endpoint URL */}
        <div className="space-y-1.5">
          <Label>Endpoint</Label>
          <div className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2">
            <code className="flex-1 font-mono text-sm break-all">{CHAT_ENDPOINT}</code>
            <Button variant="ghost" size="sm" className="shrink-0 h-7 px-2" onClick={copyUrl} aria-label="Copy endpoint URL">
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Language picker */}
        <div className="space-y-1.5">
          <Label htmlFor="snippet-lang">Language</Label>
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(SNIPPET_LABELS) as SnippetLang[]).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                  lang === l
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-input hover:border-foreground"
                }`}
              >
                {SNIPPET_LABELS[l]}
              </button>
            ))}
          </div>
        </div>

        {/* Code block */}
        <div className="relative">
          <pre className="rounded-md border bg-muted p-4 font-mono text-xs overflow-x-auto whitespace-pre leading-relaxed">
            {snippet}
          </pre>
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 h-7 px-2"
            onClick={copySnippet}
            aria-label="Copy snippet"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

type ApiKeysListProps = {
  title: string;
  description: string;
  section: Section;
  state: SectionState;
  onRefresh: () => void;
  onRevoke: (key: ApiKeyListItem) => void;
};

const ApiKeysList = ({
  title,
  description,
  state,
  onRefresh,
  onRevoke,
}: ApiKeysListProps) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onRefresh} disabled={state.loading}>
            {state.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {state.error && (
          <p className="text-sm text-destructive mb-3">{state.error}</p>
        )}

        {state.loading && state.keys.length === 0 ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : state.keys.length === 0 ? (
          <p className="text-sm text-muted-foreground">No API keys yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2 pr-4">Expires</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {state.keys.map((key) => {
                  const status = statusOf(key);
                  const variant: "default" | "secondary" | "destructive" =
                    status.tone === "active"
                      ? "default"
                      : status.tone === "danger"
                        ? "destructive"
                        : "secondary";
                  return (
                    <tr key={key.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">{key.name}</td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {formatDate(key.createdAt)}
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {formatDate(key.expiresAt)}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant={variant}>{status.label}</Badge>
                      </td>
                      <td className="py-2 pr-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRevoke(key)}
                          disabled={key.isRevoked || key.isExpired}
                          aria-label={`Revoke ${key.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
